import { ReactNode, useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import AnnouncementBar from './AnnouncementBar';
import UserStatusBar from './UserStatusBar';
import AccountMenu from './AccountMenu';
import EmailVerificationBanner from './EmailVerificationBanner';
import NotificationBell from './NotificationBell';
import { Menu, X, AlertTriangle, Sparkles, LogIn, ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

type Role = 'guest' | 'user' | 'admin';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { user, loading: authLoading, signOut } = useAuth();
  const userId = user?.id ?? null;
  const { genCredits, cashCredits, loading: creditsLoading } = useCredits();
  const { itemCount: cartCount, totalCents: cartTotalCents } = useCart();

  const [userRole, setUserRole] = useState<Role>('guest');
  const [userTier, setUserTier] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeGenerationCount, setActiveGenerationCount] = useState<number>(0);

  const isAdmin = useMemo(() => userRole === 'admin', [userRole]);
  const loading = useMemo(() => authLoading || creditsLoading || profileLoading, [authLoading, creditsLoading, profileLoading]);

  useEffect(() => {
    let cancelled = false;

    async function loadUserProfile(currentUserId: string | null) {
      if (!currentUserId) {
        if (!cancelled) {
          setUserRole('guest');
          setProfileLoading(false);
        }
        return;
      }

      setProfileLoading(true);

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('role, tier')
          .eq('user_id', currentUserId)
          .maybeSingle();

        if (error) {
          if (!cancelled) {
            setUserRole('user');
            setUserTier('free');
            setProfileLoading(false);
          }
          return;
        }

        if (!cancelled) {
          const role = (data?.role as Role | null) ?? 'user';
          const tier = data?.tier ?? 'free';
          setUserRole(role);
          setUserTier(tier);
          setProfileLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setUserRole('user');
          setProfileLoading(false);
        }
      }
    }

    loadUserProfile(userId);

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    let interval: number | null = null;

    const poll = async () => {
      if (!userId) {
        if (!cancelled) setActiveGenerationCount(0);
        return;
      }

      try {
        const { count, error } = await supabase
          .from('generation_jobs')
          .select('id', { count: 'exact', head: true })
          .in('status', ['queued', 'processing']);

        if (error) {
          return;
        }

        if (!cancelled) setActiveGenerationCount(typeof count === 'number' ? count : 0);
      } catch {
        // Error handled silently
      }
    };

    void poll();
    interval = window.setInterval(poll, 15000);

    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [userId]);

  const handleLogout = async () => {
    try {
      await signOut();
      setUserRole('guest');
      setUserTier(null);
      navigate('/', { replace: true });
    } catch {
      // Error handled silently
    }
  };

  const handleLoginClick = () => {
    const from = location.pathname + location.search;
    navigate('/auth', { state: { from } });
  };

  const handleSignupClick = () => {
    navigate('/auth?mode=signup&tier=free');
  };

  return (
    <div className="min-h-screen text-gray-700 flex flex-col">
      <AnnouncementBar />

      <nav className="border-b border-gray-200 bg-white/95 backdrop-blur-xl sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <span className="text-xs font-black tracking-tight text-white">FB</span>
              </div>
              <div className="leading-tight">
                <div className="text-sm font-bold tracking-tight text-gray-900">
                  ForgeBlocks
                </div>
                <div className="text-[10px] text-gray-500">
                  3D asset generator & marketplace
                </div>
              </div>
            </Link>

            <div className="hidden md:flex items-center gap-2 text-sm font-semibold">
              <Link
                to="/select-generator"
                className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-white shadow-sm hover:bg-orange-700 transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Generate
              </Link>
              <Link
                to="/marketplace"
                className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-gray-800 hover:bg-gray-50 transition-colors"
              >
                Marketplace
              </Link>
              {user && (
                <Link
                  to="/assets"
                  className={`px-3 py-2 rounded-xl transition-colors ${
                    location.pathname.startsWith('/assets') || location.pathname.startsWith('/my-assets')
                      ? 'text-gray-900 bg-gray-100'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Library
                </Link>
              )}
              {user && (
                <Link
                  to="/redeem"
                  className={`px-3 py-2 rounded-xl transition-colors ${
                    location.pathname.startsWith('/redeem')
                      ? 'text-gray-900 bg-gray-100'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Redeem
                </Link>
              )}
              <Link
                to="/pricing"
                className={`px-3 py-2 rounded-xl transition-colors ${
                  location.pathname === '/pricing'
                    ? 'text-gray-900 bg-gray-100'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Pricing
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user && activeGenerationCount > 0 && (
              <Link
                to="/jobs"
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-800 hover:bg-orange-100 transition-colors"
                title="View running jobs"
              >
                <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
                {activeGenerationCount} generating
              </Link>
            )}

            {user && <NotificationBell />}

            <Link
              to="/cart"
              className="hidden md:inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              title="View cart"
            >
              <ShoppingCart className="h-4 w-4" />
              {cartCount > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <span>{cartCount}</span>
                  <span className="text-xs font-normal text-gray-500">
                    {formatCurrency(cartTotalCents)}
                  </span>
                </span>
              ) : (
                <span className="text-xs font-normal text-gray-500">Empty</span>
              )}
            </Link>

            <div className="hidden md:block">
              <UserStatusBar
                user={user}
                isAdmin={isAdmin}
                genCredits={genCredits}
                cashCredits={cashCredits}
                loading={loading}
                tier={userTier}
                onLogout={handleLogout}
                onLoginClick={handleLoginClick}
                onSignupClick={handleSignupClick}
              />
            </div>

            <div className="md:hidden flex items-center gap-2">
              {user ? (
                <AccountMenu
                  user={user}
                  tier={userTier}
                  genCredits={genCredits}
                  cashCredits={cashCredits}
                  isAdmin={isAdmin}
                  onLogout={handleLogout}
                />
              ) : (
                <button
                  type="button"
                  onClick={handleLoginClick}
                  className="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                </button>
              )}
              <Link
                to="/select-generator"
                className="inline-flex items-center justify-center rounded-xl bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 transition-colors"
              >
                Generate
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                aria-label="Open menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="max-w-6xl mx-auto px-4 py-4 space-y-2">
              <Link
                to="/marketplace"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  location.pathname.startsWith('/marketplace')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Marketplace
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all relative ${
                  location.pathname === '/pricing'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  location.pathname === '/contact'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                Contact
              </Link>
              <Link
                to="/cart"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                Cart {cartCount > 0 ? `(${cartCount})` : ''}
              </Link>

              {user && (
                <>
                  <div className="pt-2 mt-2 border-t border-gray-200 text-[11px] font-semibold text-gray-500 uppercase tracking-wide px-4">
                    Your workspace
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname === '/dashboard'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/assets"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/assets') || location.pathname.startsWith('/my-assets')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Library
                  </Link>
                  <Link
                    to="/jobs"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/jobs')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Jobs
                  </Link>
                  <Link
                    to="/upload"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/upload')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Upload
                  </Link>
                  <Link
                    to="/wallet"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/wallet')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Wallet
                  </Link>
                  <Link
                    to="/credits"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/credits')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Buy tokens
                  </Link>
                  <Link
                    to="/billing"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/billing')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Billing
                  </Link>
                  <Link
                    to="/account/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/account/profile')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Settings
                  </Link>
                </>
              )}

              {isAdmin && (
                <>
                  <div className="pt-2 mt-2 border-t border-gray-200 text-[11px] font-semibold text-purple-600 uppercase tracking-wide px-4">
                    Admin
                  </div>
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/admin')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Admin dashboard
                  </Link>
                  <Link
                    to="/admin/tiers"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      location.pathname.startsWith('/admin/tiers')
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    Tier settings
                  </Link>
                  <Link
                    to="/debug-user"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Debug user
                  </Link>
                  <Link
                    to="/supabase-test"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Supabase tests
                  </Link>
                </>
              )}

            </div>
          </div>
        )}
      </nav>

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Phase 3: Low Credit Alert Banner */}
      {user && genCredits > 0 && genCredits <= 5 && !creditsLoading && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 px-4 py-2.5">
          <div className="max-w-6xl mx-auto flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="w-4 h-4" />
              <span>
                Running low! You have <strong className="font-bold">{genCredits} token{genCredits !== 1 ? 's' : ''}</strong> remaining
              </span>
            </div>
            <button
              onClick={() => navigate('/credits')}
              className="bg-amber-600 text-white px-4 py-1.5 rounded-full hover:bg-amber-700 transition-colors font-semibold text-xs shadow-sm"
            >
              Buy Tokens →
            </button>
          </div>
        </div>
      )}

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 py-6">{children}</div>
      </main>

      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} ForgeBlocks. All rights reserved.</p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="text-gray-700 font-semibold hover:text-gray-900 transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/pricing"
              className="text-gray-700 font-semibold hover:text-gray-900 transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/privacy"
              className="text-gray-700 font-semibold hover:text-gray-900 transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="text-gray-700 font-semibold hover:text-gray-900 transition-colors"
            >
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
